// Sending an answer or instruction from a linked browser (docs/07 "Inbound"): encrypt
// under the newest segment key this device holds, sign, push over the device socket, and
// wait for the relay's `sent` acknowledgement.
import { type Cek, sealInbound } from "../device/crypto";
import type { LinkedDevice } from "../device/link";
import { messages } from "../i18n";
import { bytesEqual, utf8 } from "../proto/bytes";
import type { EventType, IdBytes } from "../proto/frames";
import type { Proto } from "../proto/index";
import { ulidToBytes } from "../proto/ulid";
import type { RelayClient } from "../relay/client";
import { type ConversationState, type EventView, nextInboundSeq, writableSegment, writableSegmentById } from "./model";

export class NoWritableSegment extends Error {
  constructor() {
    super(messages().conversation.errors.noWritableSegment);
    this.name = "NoWritableSegment";
  }
}

export interface SendResult {
  messageId: IdBytes;
  delivered: boolean;
  /** The message as this device will show it in its own view. */
  event: EventView;
}

export interface SendInput {
  conversationId: string;
  eventType: Extract<EventType, "answer" | "instruction">;
  text: string;
  /** A particular open segment (a chat turn's); the newest writable one by default. */
  segmentId?: string;
  /**
   * The newest segment this device holds the key of, closed or not (D85: an approval's note,
   * read by the daemon for the next step rather than by a live worker).
   */
  anySegment?: boolean;
}

export interface Sender {
  proto: Proto;
  relay: RelayClient;
  device: LinkedDevice;
}

export async function sendInbound(
  sender: Sender,
  state: ConversationState,
  input: SendInput,
  timeoutMs = 10_000,
): Promise<SendResult> {
  const segment =
    input.segmentId !== undefined
      ? writableSegmentById(state, input.segmentId)
      : input.anySegment
        ? newestKeyedSegment(state)
        : writableSegment(state);
  if (!segment) throw new NoWritableSegment();
  const cek: Cek = segment.key.cek;
  const seq = nextInboundSeq(state, segment.id);
  const message = await sealInbound(sender.proto, sender.device.keys, sender.device.deviceIdBytes, cek, {
    conversationId: ulidToBytes(input.conversationId),
    segmentId: ulidToBytes(segment.id),
    seq,
    eventType: input.eventType,
    plaintext: utf8.encode(input.text),
  });
  const acked = awaitSent(sender.relay, message.message_id, timeoutMs);
  sender.relay.send({ type: "send", ...message });
  const { delivered } = await acked;
  const event: EventView = {
    segmentId: segment.id,
    seq,
    type: input.eventType,
    direction: "inbound",
    meta: {},
    content: { kind: "plaintext", text: input.text },
    raw: message.event,
  };
  return { messageId: message.message_id, delivered, event };
}

function awaitSent(relay: RelayClient, messageId: IdBytes, timeoutMs: number): Promise<{ delivered: boolean }> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      off();
      reject(new Error(messages().conversation.errors.notAcknowledged));
    }, timeoutMs);
    const off = relay.onFrame((frame) => {
      if (frame.type !== "sent" || !bytesEqual(frame.message_id, messageId)) return;
      clearTimeout(timer);
      off();
      resolve({ delivered: frame.delivered });
    });
  });
}

/** The newest segment whose key this device unwrapped, open or closed. */
function newestKeyedSegment(state: ConversationState): ReturnType<typeof writableSegment> {
  const keyed = Array.from(state.segments.values()).filter(
    (s): s is NonNullable<ReturnType<typeof writableSegment>> => s.key.kind === "unwrapped",
  );
  return keyed.at(-1);
}
